import React, {
  Children,
  MutableRefObject,
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

import MenuLayout, { MenuVariant } from '../MenuLayout'
import MenuList from '../MenuList'
import Popover, { PopoverProps } from '../Popover'
import styles from './Menu.module.scss'

import { useTimeout } from '~hooks/useTimeout'
import { isFunction, stopPropagation } from '~lib/helpers'
import Text from '~lib/ui-kit/Text'
import Button from '~lib/ui-kit/buttons/Button'
import usePossibleControlledState from '~shared/lib/usePossibleControlledState'

type PopperProps = Pick<
  PopoverProps,
  | 'anchorEl'
  | 'appendTo'
  | 'open'
  | 'placement'
  | 'followCursor'
  | 'onChangeOpen'
  | 'width'
  | 'maxWidth'
  | 'minWidth'
  | 'offset'
  | 'lockBody'
>

export interface MenuProps extends PopperProps, React.HTMLAttributes<HTMLUListElement> {
  variant?: MenuVariant
  creatable?: boolean
  creationLoading?: boolean
  canCreate?: ((v: string) => boolean) | boolean
  focusOnClose?: boolean
  searchable?: boolean
  disableInnerSearch?: boolean // TODO - fix inner search
  searchPlaceholder?: string
  trigger?: PopoverProps['trigger']
  onCreate?: (v: string) => void
  onSearch?: (v: string) => void
  withScroll?: boolean
  label?: string
  onKeyDown?: (ev: React.KeyboardEvent) => void
  multiple?: boolean
  nextFocusRef?: MutableRefObject<any>
}

const Menu = forwardRef<HTMLUListElement, MenuProps>(
  (
    {
      anchorEl,
      children,
      creatable,
      canCreate,
      focusOnClose = true,
      followCursor,
      open,
      onChangeOpen,
      onCreate,
      creationLoading,
      onSearch,
      placement = 'bottom-start',
      trigger = 'click',
      appendTo = 'parent',
      searchable,
      disableInnerSearch,
      withScroll = true,
      label,
      offset,
      variant,
      placeholder = 'Search …',
      width = 'auto',
      maxWidth,
      minWidth,
      onKeyDown,
      lockBody,
      multiple,
      nextFocusRef,
      ...props
    },
    ref
  ) => {
    const [openState, handleChangeOpen] = usePossibleControlledState(false, open, onChangeOpen)
    const [activeIndex, setActiveIndex] = useState(-1)
    const [menuElement, setMenuElement] = useState<HTMLUListElement>(null)
    const [inputElement, setInputElement] = useState<HTMLInputElement>(null)
    const [inputValue, setInputValue] = useState('')
    const { setTimeout } = useTimeout()

    useImperativeHandle(ref, () => menuElement)

    useEffect(() => {
      if (openState && searchable) {
        setTimeout(() => {
          inputElement?.focus()
        })
      }

      if (openState && !searchable) {
        setTimeout(() => {
          menuElement?.focus()
        })
      }

      if (searchable && !openState) {
        setInputValue('')
      }
    }, [anchorEl, inputElement, menuElement, openState, searchable, setTimeout])

    function handleClickItem(ev: React.MouseEvent<HTMLElement>) {
      if (ev.currentTarget.getAttribute('role') === 'none') return

      if (ev.currentTarget.getAttribute('aria-haspopup') === 'true') {
        const idx = Number(ev.currentTarget.dataset.index)

        if (!Number.isNaN(idx)) {
          setActiveIndex(idx)
        }
      } else if (!multiple) {
        if (focusOnClose) anchorEl.focus()
        handleChangeOpen(false)
      }
    }

    function handleMouseEnterItem(e: React.MouseEvent<HTMLAnchorElement>) {
      const idx = Number(e.currentTarget.dataset.index)

      if (!Number.isNaN(idx)) {
        setActiveIndex(idx)
      }
    }

    function handleMouseLeaveItem() {
      setActiveIndex(-1)
    }

    const sanitizedInputValue = inputValue.toLowerCase().trim()

    const displayItems = openState
      ? Children.toArray(children)
          .filter((child) => {
            if (!sanitizedInputValue || disableInnerSearch) {
              return true
            }

            if (isValidElement(child)) {
              const { children, label } = child.props

              return (
                (typeof children === 'string' && children.toLowerCase().includes(sanitizedInputValue)) ||
                (typeof label === 'string' && label.toLowerCase().includes(sanitizedInputValue)) ||
                (typeof child.props['data-search-value'] === 'string' && // Fix menu to allow seach by value
                  child.props['data-search-value'].toLowerCase().includes(sanitizedInputValue)) ||
                // in case if menu item wrapped in (inside) something (React.Fragment, etc) we should use smth like children.props depending on the nested structure
                (typeof children?.[0]?.props?.['data-search-value'] === 'string' &&
                  children?.[0]?.props?.['data-search-value'].toLowerCase().includes(sanitizedInputValue))
              )
            }

            return false
          })
          .map((node: React.ReactElement, index) => {
            // FIXME use context instead of cloneElement
            return cloneElement(node, {
              'data-index': index,
              active: index === activeIndex || undefined,
              tabIndex: index && -1,
              onMouseEnter: handleMouseEnterItem,
              onMouseLeave: handleMouseLeaveItem,
              onClick: (ev: React.MouseEvent<HTMLElement>) => {
                // ev.persist()
                handleClickItem(ev)
                node.props.onClick?.(ev)
              },
            })
          })
      : []

    const canCreateEntity = (() => {
      if (!creatable) return false
      if (typeof canCreate === 'boolean') return canCreate
      if (isFunction(canCreate)) return canCreate(sanitizedInputValue)
      if (!sanitizedInputValue) return false
      return !displayItems.some((child) => child.props.children.toLowerCase() === sanitizedInputValue)
    })()

    function handleBlurMenu(e: React.FocusEvent<HTMLUListElement>) {
      e.stopPropagation()
      setActiveIndex(-1)
    }

    function handleChangeInput(e: React.ChangeEvent<HTMLInputElement>) {
      setActiveIndex(0)
      setInputValue(e.currentTarget.value)
      onSearch?.(e.currentTarget.value)
    }

    function handleFocusMenu(e: React.FocusEvent<HTMLUListElement>) {
      e.preventDefault()
      e.stopPropagation()

      setActiveIndex((prevState) => {
        return prevState > 0 ? prevState : 0
      })
    }

    function handleCreateEntity() {
      onCreate?.(inputValue.trim())
      setInputValue('')
    }

    function handleKeyDownMenu(e: React.KeyboardEvent) {
      let handled = false

      if (e.key === 'ArrowDown') {
        handled = true
        const total = displayItems.length
        const nextIdx = activeIndex >= total - 1 ? 0 : activeIndex + 1
        menuElement?.querySelector(`[data-index="${nextIdx}"]`)?.scrollIntoView({ block: 'nearest' })
        setActiveIndex(nextIdx)
      }

      if (e.key === 'ArrowUp') {
        handled = true
        const total = displayItems.length
        const nextIdx = activeIndex <= 0 ? total - 1 : activeIndex - 1
        menuElement?.querySelector(`[data-index="${nextIdx}"]`)?.scrollIntoView({ block: 'nearest' })
        setActiveIndex(nextIdx)
      }

      if (e.key === 'Enter' && displayItems.length === 0 && canCreateEntity) {
        e.preventDefault()
        e.stopPropagation()
        handleCreateEntity()
      }

      if (e.key === 'Enter' || e.key === 'ArrowRight' || e.key === 'Space') {
        handled = true
        const elem =
          menuElement.querySelector<HTMLElement>(`[role="menuitem"][data-index="${activeIndex}"]`) ||
          menuElement.querySelector<HTMLElement>(`[role="option"][data-index="${activeIndex}"]`)
        elem?.click()
      }

      if (e.key === 'Escape' || e.key === 'ArrowLeft') {
        handled = true
        if (focusOnClose) anchorEl?.focus()
        handleChangeOpen(false)
      }

      if (e.key === 'Tab') {
        if (nextFocusRef) {
          handled = true
          nextFocusRef?.current.focus()
          handleChangeOpen(false)
        } else {
          handled = true
          if (focusOnClose) anchorEl?.focus()
          handleChangeOpen(false)
        }
      }

      if (e.key === 'Home') {
        handled = true
        const nextIdx = 0
        menuElement?.querySelector(`[data-index="${nextIdx}"]`)?.scrollIntoView({ block: 'nearest' })
        setActiveIndex(nextIdx)
      }

      if (e.key === 'End') {
        handled = true
        const total = displayItems.length
        const nextIdx = total - 1
        menuElement?.querySelector(`[data-index="${nextIdx}"]`)?.scrollIntoView({ block: 'nearest' })
        setActiveIndex(nextIdx)
      }

      if (handled) {
        e.preventDefault()
        e.stopPropagation()
      } else {
        onKeyDown?.(e)
      }
    }

    useEffect(() => {
      if (!menuElement || !withScroll || !openState) return
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(({ target }) => {
          const scrollbarVisible = target.scrollHeight > target.clientHeight
          if (scrollbarVisible) {
            target.classList.add(styles.menuWithScroll)
          } else {
            target.classList.remove(styles.menuWithScroll)
          }
        })
      })
      resizeObserver.observe(menuElement)
      // eslint-disable-next-line consistent-return
      return () => resizeObserver.disconnect()
    }, [menuElement, openState, withScroll])

    return (
      <Popover
        appendTo={appendTo}
        anchorEl={anchorEl}
        followCursor={followCursor}
        onChangeOpen={handleChangeOpen}
        open={openState}
        placement={placement}
        trigger={trigger}
        offset={offset}
        width={width}
        maxWidth={maxWidth}
        minWidth={minWidth}
        lockBody
      >
        <MenuLayout variant={variant}>
          {searchable && (
            <MenuLayout.Header>
              <input
                className={styles.input}
                ref={setInputElement}
                type="text"
                value={inputValue}
                placeholder={placeholder}
                onChange={handleChangeInput}
                onKeyDown={handleKeyDownMenu}
                onClick={stopPropagation}
              />
            </MenuLayout.Header>
          )}
          {label && (
            <Text className={styles.label} variant="secondary" type="caption">
              {label}
            </Text>
          )}

          <MenuList
            ref={setMenuElement}
            onBlur={handleBlurMenu}
            onFocus={handleFocusMenu}
            onKeyDown={handleKeyDownMenu}
            scrollActive={withScroll}
            {...props}
          >
            {displayItems}
          </MenuList>

          {canCreateEntity && (
            <Button variant="secondary" size="md" onClick={handleCreateEntity}>
              + Add {sanitizedInputValue && <>&quot;{sanitizedInputValue}&quot;</>}
            </Button>
          )}
        </MenuLayout>
      </Popover>
    )
  }
)

export default Object.assign(Menu, {
  Item: MenuList.Item,
  Divider: MenuList.Divider,
})
